import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';
import { Player } from '@/lib/types';

interface PlayerDeletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  players: Player[];
  onConfirm: (reason: 'not_interested' | 'duplicate' | 'not_a_player') => void;
  isLoading?: boolean;
}

export function PlayerDeletionDialog({
  open,
  onOpenChange,
  players,
  onConfirm,
  isLoading = false,
}: PlayerDeletionDialogProps) {
  const [reason, setReason] = useState<'not_interested' | 'duplicate' | 'not_a_player'>('not_interested');

  const handleConfirm = () => {
    onConfirm(reason);
  };

  const playerCount = players.length;
  const isMultiple = playerCount > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            Remove {isMultiple ? `${playerCount} Players` : 'Player'}
          </DialogTitle>
          <DialogDescription>
            {isMultiple
              ? `Why do you want to remove these ${playerCount} players from your database?`
              : `Why do you want to remove ${players[0]?.name || 'this player'} from your database?`}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <RadioGroup value={reason} onValueChange={(value) => setReason(value as any)}>
            <div className="flex items-center space-x-2 py-2">
              <RadioGroupItem value="not_interested" id="not_interested" />
              <Label htmlFor="not_interested" className="cursor-pointer">
                I'm not interested
              </Label>
            </div>
            <div className="flex items-center space-x-2 py-2">
              <RadioGroupItem value="duplicate" id="duplicate" />
              <Label htmlFor="duplicate" className="cursor-pointer">
                It's a duplicate
              </Label>
            </div>
            <div className="flex items-center space-x-2 py-2">
              <RadioGroupItem value="not_a_player" id="not_a_player" />
              <Label htmlFor="not_a_player" className="cursor-pointer">
                It's not a player
              </Label>
            </div>
          </RadioGroup>
        </div>

        <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
          <p>
            {isMultiple ? 'These players' : 'This player'} will be moved to your "Deleted Players" tab where you can restore {isMultiple ? 'them' : 'it'} later if needed.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            variant="destructive"
          >
            {isLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}